import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxPopupService } from '../../../shared/ngx-popups/ngx-popups/services/ngx-popup.service';
import { ConfirmationPopupComponent } from '../../../shared/popups/confirmation/confirmation-popup.component';
import { FileImportModalComponent } from '../../../shared/popups/file-import-modal/file-import-modal.component';
import { SpreadingTemplateService } from '../../../../services/spreading-template.service';
import { AlertService } from '../../../../services/alert.service';
import { SharedDataService } from '../../../../services/shared-data.service';
import {
  TemplateDataService,
  TemplateData,
  SubmissionRule,
  SelectOption,
  ActionOption,
  WorkingNode,
  SaveSubmissionRule,
  SubmissionRuleActions,
  SubmissionRuleConditions
} from '../../../../services/template-data.service';

@Component({
    selector: 'app-edit-submission-rules',
    templateUrl: './edit-submission-rules.component.html',
    styleUrls: ['./edit-submission-rules.component.scss'],
    standalone: false
})
export class EditSubmissionRulesComponent implements OnInit, OnDestroy {
  private readonly SUCCESS_MESSAGE = 'Submission rules saved successfully!';
  private readonly FAILED_SAVE_MESSAGE = 'Failed to save submission rules: ';
  private readonly VALIDATION_ERROR_MESSAGE = 'Please fill in all required fields before saving.';
  private readonly TEMPLATE_DATA_ERROR_MESSAGE = 'Template data not found. Please make sure the template exists.';
  private readonly REF_NOT_EXIST_MESSAGE = 'Ref does not exist in current template config';
  private readonly NO_RULES_TO_EXPORT_MESSAGE = 'No submission rules to export, please create some rules first.';
  private readonly UNSAVED_CHANGES_EXPORT_MESSAGE = 'You have unsaved changes. Do you want to save and export?';
  private readonly EXPORT_SUCCESS_MESSAGE = 'Export successfully.';
  private readonly EXPORT_FAILED_MESSAGE = 'Export failed.';
  private readonly IMPORT_OVERRIDE_MESSAGE = 'This will override all existing submission rules, are you sure you want to import?';
  private readonly IMPORT_SUCCESS_MESSAGE = 'Submission rules imported successfully!';
  private readonly IMPORT_FAILED_MESSAGE = 'Import failed: ';
  private readonly FILE_NOT_CSV_MESSAGE = 'Please select a valid CSV file.';
  private readonly FILE_EMPTY_MESSAGE = 'The selected file is empty.';
  private readonly INVALID_CSV_MESSAGE = 'The file contains invalid CSV format.';
  private readonly INVALID_SUBMISSION_RULES_FORMAT_MESSAGE = 'The file does not contain valid submission rules format.';
  private readonly IMPORT_DESCRIPTION = 'Import a CSV file containing REF to Submission Rule Mappings. A file can be created manually, or by isolating the relevant columns in an Excel spreadsheet and "saving as" a CSV file. Do not include any header rows or extraneous data as all rows in the file will be interpreted as mapping definitions.';

  templateUuid: string;
  templateType: string;
  templateName: string;
  templateId: number;
  rulesCount: number = 0;
  errorCount: number = 0;
  templateData: TemplateData;

  // Submission rules data loaded from template and display on UI
  submissionRules: SubmissionRule[] = [];

  // Available template line items for dropdown loaded from template data
  availableTemplateItems: SelectOption[] = [];

  availableConditions: SelectOption[] = [
    { id: SubmissionRuleConditions.EQUAL_TO, text: '= Equal to' },
    { id: SubmissionRuleConditions.NOT_EQUAL_TO, text: '≠ Not Equal to' },
    { id: SubmissionRuleConditions.GREATER_THAN, text: '> Greater than' },
    { id: SubmissionRuleConditions.LESS_THAN, text: '< Less than' },
    { id: SubmissionRuleConditions.GREATER_THAN_OR_EQUAL_TO, text: '≥ Greater than or equal to' },
    { id: SubmissionRuleConditions.LESS_THAN_OR_EQUAL_TO, text: '≤ Less than or equal to' }
  ];

  availableActions: ActionOption[] = [
    {
      id: SubmissionRuleActions.HARD_STOP,
      text: 'Insufficient: Hard Stop',
      additional: 'Display as red and force user to resolve before completing spread'
    },
    {
      id: SubmissionRuleActions.SOFT_STOP,
      text: 'Insufficient: Soft Stop',
      additional: 'Display as yellow and allow user to complete spread'
    }
  ];

  isDirty: boolean = false;
  isLoading: boolean = false;
  canCancelState: boolean = false;
  canSaveState: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private popupService: NgxPopupService,
    private spreadingTemplateService: SpreadingTemplateService,
    private alertService: AlertService,
    private sharedDataService: SharedDataService,
    private templateDataService: TemplateDataService
  ) { }

  ngOnInit(): void {
    this.sharedDataService.pageHeaderTitle$.next('Edit Submission Rules');
    this.route.params.subscribe(params => {
      this.templateUuid = params['template_uuid'];
      this.templateType = params['template_type'];
    });
    this.loadTemplateData();
    this.updateStates();
  }

  ngOnDestroy(): void {
    this.templateDataService.clearTemplateData();
  }

  goBack(): void {
    this.templateDataService.clearTemplateData();
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  hasUnsavedChanges(): boolean {
    if (!this.templateData?.spreadingTemplate?.submissionRules) {
      return this.submissionRules.length > 0;
    }

    const originalRules = this.templateData.spreadingTemplate.submissionRules;
    if (this.submissionRules.length !== originalRules.length) {
      return true;
    }

    // Check if any rule content has changed
    for (let i = 0; i < this.submissionRules.length; i++) {
      const currentRule = this.submissionRules[i];
      const originalRule = originalRules[i];

      if (!originalRule) {
        // New rule added
        return true;
      }

      if (currentRule.templateItem !== originalRule.templateItem ||
          currentRule.condition !== originalRule.condition ||
          currentRule.equation !== originalRule.equation ||
          currentRule.action !== originalRule.action) {
        return true;
      }
    }

    return false;
  }

  addSubmissionRule(): void {
    const newRule: SubmissionRule = {
      id: this.submissionRules.length + 1,
      templateItem: '',
      condition: null,
      equation: '',
      action: SubmissionRuleActions.HARD_STOP,
      hasError: false
    };
    this.submissionRules.push(newRule);
    this.rulesCount = this.submissionRules.length;
    this.updateErrorCount();
    this.isDirty = true;
    this.updateStates();
  }

  deleteSubmissionRule(ruleId: number): void {
    this.submissionRules = this.submissionRules.filter(rule => rule.id !== ruleId);
    this.rulesCount = this.submissionRules.length;
    this.updateErrorCount();
    this.isDirty = true;
    this.updateStates();
  }

  onTemplateItemChange(rule: SubmissionRule, newValue: string): void {
    rule.templateItem = newValue;
    rule.hasError = !this.availableTemplateItems.some(item => item.id === newValue);
    if (rule.hasError) {
      rule.errorMessage = this.REF_NOT_EXIST_MESSAGE;
    } else {
      rule.errorMessage = undefined;
    }
    this.updateErrorCount();
    this.isDirty = true;
    this.updateStates();
  }

  onConditionChange(rule: SubmissionRule, newValue: SubmissionRuleConditions): void {
    rule.condition = newValue;
    this.isDirty = true;
    this.updateStates();
  }

  onActionChange(rule: SubmissionRule, newValue: SubmissionRuleActions): void {
    rule.action = newValue;
    this.isDirty = true;
    this.updateStates();
  }

  onEquationChange(rule: SubmissionRule, newValue: string): void {
    rule.equation = newValue;
    this.isDirty = true;
    this.updateStates();
  }

  canCancel(): boolean {
    const canCancel = this.isDirty || this.hasUnsavedChanges();
    this.canCancelState = canCancel;
    return canCancel;
  }

  trackByRuleId(index: number, rule: SubmissionRule): number {
    return rule.id;
  }

  cancelChanges(): void {
    if (this.hasUnsavedChanges()) {
      this.popupService.open({
        componentType: ConfirmationPopupComponent,
        cssClass: 'modal-confirmation',
        inputs: {
          question: 'CONFIRM CANCEL',
          text: 'Are you sure you want to cancel? Any unsaved changes will be lost.',
          confirmButtonText: 'Yes'
        },
        outputs: {
          callback: (confirmed: boolean) => {
            if (confirmed) {
              this.resetToOriginalState();
            }
          }
        },
      });
    } else {
      this.resetToOriginalState();
    }
  }

  saveRules(): void {
    if (!this.canSaveState || this.isLoading) {
      this.alertService.error(this.VALIDATION_ERROR_MESSAGE);
      return;
    }
    this.performSaveRules();
  }

  saveRulesAndExitWithCallback(resolve: (approved: boolean) => void, isImport: boolean = false): void {
    if (!this.canSaveState || this.isLoading) {
      this.alertService.error(this.VALIDATION_ERROR_MESSAGE);
      resolve(false);
      return;
    }
    this.executeSaveRules(
      () => {
        // Successfully saved
        if (!isImport) {
          this.alertService.success(this.SUCCESS_MESSAGE);
        }
        this.isDirty = false;
        this.isLoading = false;
        this.updateStates();
        resolve(true);
      },
      (error: { message: string }) => {
        // Errored out
        this.alertService.error(this.FAILED_SAVE_MESSAGE + error.message);
        this.isLoading = false;
        resolve(false);
      }
    );
  }

  importRules(): void {
    if (this.submissionRules.length > 0) {
      // Show confirmation dialog if there are existing rules
      this.popupService.open({
        componentType: ConfirmationPopupComponent,
        cssClass: 'modal-confirmation',
        inputs: {
          question: 'CONFIRM IMPORT',
          text: this.IMPORT_OVERRIDE_MESSAGE,
          confirmButtonText: 'Yes',
          cancelButtonText: 'Cancel'
        },
        outputs: {
          callback: (confirmed: boolean) => {
            if (confirmed) {
              this.openImportModal();
            }
          }
        },
      });
    } else {
      // No existing rules, directly open import modal
      this.openImportModal();
    }
  }

  private openImportModal(): void {
    const warningText = this.submissionRules.length > 0
      ? 'Note: this will overwrite all submission rules for this template.'
      : undefined;

    this.popupService.open({
      componentType: FileImportModalComponent,
      inputs: {
        title: 'UPLOAD CSV FILE',
        description: this.IMPORT_DESCRIPTION,
        warningText: warningText,
        acceptedFileTypes: '.csv',
        fileTypeDescription: 'CSV file'
      },
      outputs: {
        fileSelected: (file: File) => {
          this.processImportFile(file);
        },
        cancelled: () => {
          // Modal closed, do nothing
        }
      },
    }).then(() => {});
  }

  private processImportFile(file: File): void {
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.alertService.error(this.FILE_NOT_CSV_MESSAGE);
      return;
    }

    // Validate file size
    if (file.size === 0) {
      this.alertService.error(this.FILE_EMPTY_MESSAGE);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const content = e.target.result;
        if (!content || content.trim() === '') {
          this.alertService.error(this.FILE_EMPTY_MESSAGE);
          return;
        }

        const csvData = this.parseCSV(content);

        // Validate CSV structure
        if (!csvData || csvData.length === 0) {
          this.alertService.error(this.INVALID_SUBMISSION_RULES_FORMAT_MESSAGE);
          return;
        }

        // Validate each submission rule
        const validatedRules: SubmissionRule[] = [];
        for (let i = 0; i < csvData.length; i++) {
          const rule = csvData[i];

          // Check if template item exists in available items
          const templateItemExists = this.availableTemplateItems.some(item => item.id === rule.templateItem);

          validatedRules.push({
            id: i + 1,
            templateItem: rule.templateItem,
            condition: rule.condition,
            equation: rule.equation,
            action: rule.action,
            hasError: !templateItemExists,
            errorMessage: !templateItemExists ? this.REF_NOT_EXIST_MESSAGE : undefined
          });
        }

        this.submissionRules = validatedRules;
        this.rulesCount = this.submissionRules.length;
        this.updateErrorCount();
        this.isDirty = true;
        this.updateStates();
        this.popupService.closeAll();
        this.alertService.success(this.IMPORT_SUCCESS_MESSAGE);
      } catch (error) {
         console.error('Error processing import file:', error);
         this.alertService.error(this.INVALID_CSV_MESSAGE);
      }
    };

    reader.onerror = () => {
      this.alertService.error(this.IMPORT_FAILED_MESSAGE + 'Failed to read file.');
    };

    reader.readAsText(file);
  }

  private parseCSV(content: string): any[] {
    const lines = content.trim().split('\n');
    if (lines.length < 2) {
      return [];
    }

    // Parse header row
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

    // Validate headers
    const requiredHeaders = ['templateItem', 'condition', 'equation', 'action'];
    for (const header of requiredHeaders) {
      if (!headers.includes(header)) {
        throw new Error(`Missing required header: ${header}`);
      }
    }

    // Parse data rows
    const data: any[] = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const values = this.parseCSVLine(line);
      if (values.length !== headers.length) {
        throw new Error(`Row ${i + 1} has incorrect number of columns`);
      }

      const row: any = {};
      headers.forEach((header, index) => {
        row[header] = values[index].replace(/"/g, '').trim();
      });
      data.push(row);
    }

    return data;
  }

  private parseCSVLine(line: string): string[] {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];

      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current);
    return result;
  }

  exportRules(): void {
    if (this.submissionRules.length === 0) {
      this.alertService.warning(this.NO_RULES_TO_EXPORT_MESSAGE);
      return;
    }
    if (this.hasUnsavedChanges()) {
      this.popupService.open({
        componentType: ConfirmationPopupComponent,
        cssClass: 'modal-confirmation',
        inputs: {
          question: 'CONFIRM EXPORT',
          text: this.UNSAVED_CHANGES_EXPORT_MESSAGE,
          confirmButtonText: 'Save and Export',
          cancelButtonText: 'Cancel'
        },
        outputs: {
          callback: (confirmed: boolean) => {
            if (confirmed) {
              this.saveAndExport();
            }
          }
        },
      });
     } else {
       this.performExport();
     }
  }

  private saveAndExport(): void {
    this.executeSaveRules(
      () => {
        // Save successful, then export
        this.performExport();
      },
      (error: { message: string }) => {
        // Save failed, show error and don't export
        this.alertService.error(this.FAILED_SAVE_MESSAGE + error.message);
      }
    );
   }

   private performExport(): void {
    try {
      // Create CSV content
      const headers = ['templateItem', 'condition', 'equation', 'action'];
      const csvRows = [headers.join(',')];

      this.submissionRules.forEach(rule => {
        const row = [
          `"${rule.templateItem}"`,
          `"${rule.condition}"`,
          `"${rule.equation}"`,
          `"${rule.action}"`
        ];
        csvRows.push(row.join(','));
      });

      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = this.getSanitizedFileName();

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      this.alertService.success(this.EXPORT_SUCCESS_MESSAGE);
    } catch (error) {
      console.error('Error exporting submission rules:', error);
      this.alertService.error(this.EXPORT_FAILED_MESSAGE);
    }
  }

  private getSanitizedFileName(): string {
    // Remove or replace invalid characters for file names
    let sanitizedName = this.templateName
      .replace(/[<>:"|?*\\/]/g, '')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    return `${sanitizedName ?? 'template'}-submission-rules.csv`;
  }

  private updateStates(): void {
    this.updateCancelState();
    this.updateSaveState();
  }

  private updateCancelState(): void {
    this.canCancelState = this.isDirty || this.hasUnsavedChanges();
  }

  private updateSaveState(): void {
    if (!this.isDirty || this.isLoading) {
      this.canSaveState = false;
      return;
    }
    this.canSaveState = this.submissionRules.every(rule =>
      rule.templateItem && rule.templateItem.trim() !== '' &&
      rule.condition && rule.condition.trim() !== '' &&
      rule.equation && rule.equation.trim() !== '' &&
      rule.action && rule.action.trim() !== ''
    );
  }

  private updateErrorCount(): void {
    this.errorCount = this.submissionRules.filter(rule => rule.hasError).length;
  }

  private performSaveRules(): void {
    this.executeSaveRules(
      () => {
        this.alertService.success(this.SUCCESS_MESSAGE);
        this.isLoading = false;
      },
      (error: { message: string }) => {
        this.alertService.error(this.FAILED_SAVE_MESSAGE + error.message);
        this.isLoading = false;
      }
    );
  }

  private executeSaveRules(
    onSuccess: () => void,
    onError: (error: { message: string }) => void
  ): void {
    if (!this.canSaveState) {
      onError({ message: this.VALIDATION_ERROR_MESSAGE });
      return;
    }

    if (!this.templateData) {
      onError({ message: this.TEMPLATE_DATA_ERROR_MESSAGE });
      return;
    }

    const submissionRulesToSave: SaveSubmissionRule[] = this.submissionRules.map(rule => ({
      templateItem: rule.templateItem,
      condition: rule.condition,
      equation: rule.equation,
      action: rule.action
    }));

    const updatePayload = {
      submissionRules: submissionRulesToSave
    };

    this.isLoading = true
    // Call spreading template service to update the template
    this.spreadingTemplateService.updateSubmissionRules(this.templateId, updatePayload).subscribe(
      () => {
        this.templateData.spreadingTemplate.submissionRules = submissionRulesToSave;
        this.templateDataService.updateTemplateData({ spreadingTemplate: this.templateData.spreadingTemplate });
        onSuccess();
        this.isLoading = false;
        this.isDirty = false;
        this.updateStates();
      },
      (error) => {
        onError(error);
        this.isLoading = false;
      }
    );
  }

  private extractTemplateLineItems(workingNodes: WorkingNode[]): SelectOption[] {
    const templateItems: SelectOption[] = [];
    const processNode = (node: WorkingNode) => {
      if (!node.children || node.children.length === 0) {
        if (node.ref && node.label) {
          if (["nonAdjustableRawAddBack", "adjustableRawAddBack"].includes(node?.dynamicAdjustmentStatementItemType)) {
            // Submission rules are not supported for add back item types
            return;
          }
          templateItems.push({
            id: node.ref,
            text: node.label
          });
        }
      } else {
        if (node.hasRollup) {
          templateItems.push({
            id: node.ref,
            text: node.rollupLabel
          });
        }
        node.children.forEach((child: WorkingNode) => processNode(child));
      }
    };
    workingNodes.forEach((node: WorkingNode) => processNode(node));
    return templateItems;
  }

  private loadTemplateData(): void {
    try {
      this.templateData = this.templateDataService.getTemplateData();
      if (!this.templateData?.spreadingTemplate) {
        // If the template data is not found in service, load it from backend
        this.loadTemplateDataFromBackend();
        return;
      }

      this.templateName = this.templateData.templateName;
      this.templateId = this.templateData.templateId;

      // Extract available template line items from template data
      this.availableTemplateItems = this.extractTemplateLineItems(this.templateData.workingNodes);

      this.loadSubmissionRulesFromTemplateData();
    } catch (error) {
      console.error('Error loading template data:', error);
      this.alertService.error('Failed to load template data. Please try again.');
    }
  }

  private loadSubmissionRulesFromTemplateData(): void {
    // Load submission rules JSON node part from template data
    if (this.templateData.spreadingTemplate && this.templateData.spreadingTemplate.submissionRules) {
      this.submissionRules = this.templateData.spreadingTemplate.submissionRules.map((rule, index) => {
        // Check if the rule line item still exists in template data, if not, append an error to remind user
        const templateItemExists = this.availableTemplateItems.some(item => item.id === rule.templateItem);
        return {
           ...rule,
           id: index + 1,
           hasError: !templateItemExists,
           errorMessage: !templateItemExists ? this.REF_NOT_EXIST_MESSAGE : undefined
         };
      });
    } else {
      this.submissionRules = [];
    }

    this.rulesCount = this.submissionRules.length;
    this.updateErrorCount();
  }

  private loadTemplateDataFromBackend(): void {
    const templateUuid = this.templateUuid;
    const templateType = this.templateType;
    this.isLoading = true;

    if (!templateUuid) {
      this.alertService.error('Template UUID not found. Please access submission rules through the template editor.');
      this.router.navigate(['../'], { relativeTo: this.route });
      return;
    }

    this.spreadingTemplateService.getTemplateByUuid(templateUuid).subscribe(
      (template) => {
        if (!template) {
          this.alertService.error('Template not found. Please access submission rules through the template editor.');
          this.router.navigate(['../'], { relativeTo: this.route });
          return;
        }

        const templateId = template.id;
        if (typeof templateId !== 'number') {
          this.alertService.error('Invalid template ID. Please access submission rules through the template editor.');
          this.router.navigate(['../'], { relativeTo: this.route });
          return;
        }

        // Load the full template data from backend if not exist in service
        // This is to handle the case when user refresh the page on submission rules
        this.spreadingTemplateService.getTemplate(templateId).subscribe(
          (templateData) => {
            try {
              this.templateData = {
                templateUuid: templateUuid,
                templateId: templateId,
                templateName: templateData.name,
                templateType: templateType,
                workingNodes: templateData.items,
                spreadingTemplate: {
                  name: templateData.name,
                  isDefault: templateData.isDefault,
                  templateSubtype: templateData.templateSubtype,
                  submissionRules: templateData.submissionRules || []
                },
                incomeStatementCommonSizeDenominatorOverrideRef: templateData.commonSizeDenominatorOverrides?.incomeStatement || '',
                balanceSheetCommonSizeDenominatorOverrideRef: templateData.commonSizeDenominatorOverrides?.balanceSheet || ''
              };

              // Store the loaded data in the service for future use
              this.templateDataService.setTemplateData(this.templateData);

              this.templateName = this.templateData.templateName;
              this.templateId = this.templateData.templateId;

              // Extract available template line items from template data
              this.availableTemplateItems = this.extractTemplateLineItems(this.templateData.workingNodes);

              this.loadSubmissionRulesFromTemplateData();
            } catch (error) {
              console.error('Error processing template data:', error);
              this.alertService.error('Failed to process template submission rules.');
            }
            finally {
              this.isLoading = false;
            }
          },
          (error) => {
            this.alertService.error('Failed to load template data.');
            this.isLoading = false;
          }
        );
      },
      (error) => {
        console.error('Error loading template by UUID:', error);
        this.alertService.error('Template not found. Please access submission rules through the template editor.');
        this.router.navigate(['../'], { relativeTo: this.route }        );
      }
    );
  }

  private resetToOriginalState(): void {
    try {
      this.loadSubmissionRulesFromTemplateData();
      this.isDirty = false;
      this.updateStates();
    } catch (error) {
      console.error('Error resetting to original state:', error);
      this.alertService.error('Failed to reset changes. Please try again.');
    }
  }
}
